// src/hooks/useLoadForms.ts
import type { LoadableComponent } from '@loadable/component';
import { useMemo } from 'react';

import { RESOURCE_FORM_MAP } from '@constants/RESOURCE_FORM_MAP';

import { mapBBoxTitleToFormType } from '@utils/mapBBoxTitleToFormType';

import type { BBoxResource } from '../../types/bbox';

/**
 * ALWAYS load all supported form types declared in RESOURCE_FORM_MAP.
 * Also load any additional types discovered from bboxData.
 *
 * This ensures the "Add Form" dropdown always contains every category,
 * even if it does not appear in the OCR results.
 *
 * Since RESOURCE_FORM_MAP uses loadable components, they will load
 * on-demand when rendered - no need to await or preload.
 */
export const useLoadForms = (bboxData: BBoxResource[]) => {
  const forms = useMemo(() => {
    // 1. Types explicitly supported in the product
    const declaredTypes = Object.keys(RESOURCE_FORM_MAP);

    // 2. Types discovered dynamically from OCR
    const detectedTypes = Array.from(
      new Set(
        bboxData
          .map((r) => mapBBoxTitleToFormType(r.Resource_title))
          .filter((t): t is string => Boolean(t))
      )
    );

    // 3. Union of both (ensures Allergies appears even without OCR)
    const allTypes = Array.from(new Set([...declaredTypes, ...detectedTypes]));

    const loaded: Record<string, LoadableComponent<any>> = {};

    allTypes.forEach((type) => {
      if (type in RESOURCE_FORM_MAP) {
        loaded[type] = RESOURCE_FORM_MAP[type];
      }
    });

    return loaded;
  }, [bboxData]);

  return { forms, loading: false };
};
